/**
 * Bot-visible view of a hand.
 *
 * This is the ONLY boundary through which decision code may see game
 * state. `BotView` must never structurally contain the opponent's hole
 * cards or any board card that has not yet been revealed for the current
 * street. `botViewFor` is the single place allowed to read `HandState`
 * fields that are hidden information (`state.players[opponent].hole`,
 * `state.deal.*` beyond the current street) -- everything downstream
 * (`equity.ts`, `strategy.ts`) only ever sees a `BotView`.
 *
 * See test/anti-cheat.test.ts for the property test that enforces this:
 * two `HandState`s that differ only in the opponent's hole cards must
 * produce deep-equal `BotView`s for both seats.
 */

import {
  legalActions,
  type ActionLogEntry,
  type Card,
  type HandConfig,
  type HandState,
  type LegalAction,
  type PlayerIndex,
  type Street,
} from "@joker-holdem/core";

/**
 * Sentinel value standing in for the joker in `BotView.board`. Never a
 * valid real `Card` (which are in [0, 52)), so it can't be confused with
 * one. The joker is always logically "the first flop card" once the flop
 * is dealt (GAME_RULES.md §1); its concrete interpretation is resolved
 * per-player only at showdown by `evaluateJoker`, which bot code never
 * needs to call directly for its own decisions (see equity.ts).
 */
export const JOKER: Card = -1;

export interface BotViewPlayer {
  readonly stack: number;
  readonly bet: number;
  readonly totalCommitted: number;
  readonly folded: boolean;
  readonly isAllIn: boolean;
}

/**
 * Everything a bot is allowed to know when it's time to decide. Own hole
 * cards, publicly revealed board cards only (joker sentinel included once
 * the flop is out), and pure betting-state information.
 */
export interface BotView {
  readonly config: HandConfig;
  /** Which seat this view is for. */
  readonly seat: PlayerIndex;
  readonly opponent: PlayerIndex;
  readonly button: PlayerIndex;
  readonly street: Street;
  /** This seat's own two hole cards -- never the opponent's. */
  readonly hole: readonly [Card, Card];
  /**
   * Revealed board cards only, in deal order. Empty preflop. From the
   * flop onward, index 0 is always `JOKER`, followed by the real cards
   * revealed so far (2 on the flop, 3 on the turn, 4 on the river).
   */
  readonly board: readonly Card[];
  /** Public per-seat betting state, indexed by seat (both seats visible -- neither hole is). */
  readonly players: readonly [BotViewPlayer, BotViewPlayer];
  readonly pot: number;
  readonly minRaiseIncrement: number;
  readonly actedThisStreet: readonly [boolean, boolean];
  readonly toAct: PlayerIndex | null;
  readonly actionLog: readonly ActionLogEntry[];
  /** Legal actions for whoever is `toAct` -- purely a function of public betting state. */
  readonly legalActions: readonly LegalAction[];
  readonly startingStacks: readonly [number, number];
  readonly isMyTurn: boolean;
}

function otherSeat(seat: PlayerIndex): PlayerIndex {
  return seat === 0 ? 1 : 0;
}

function visibleBoardCards(deal: HandState["deal"], street: Street): Card[] {
  switch (street) {
    case "preflop":
      return [];
    case "flop":
      return [JOKER, deal.flopReal[0], deal.flopReal[1]];
    case "turn":
      return [JOKER, deal.flopReal[0], deal.flopReal[1], deal.turn];
    case "river":
      return [JOKER, deal.flopReal[0], deal.flopReal[1], deal.turn, deal.river];
  }
}

function publicPlayer(p: HandState["players"][number]): BotViewPlayer {
  return {
    stack: p.stack,
    bet: p.bet,
    totalCommitted: p.totalCommitted,
    folded: p.folded,
    isAllIn: p.isAllIn,
  };
}

/**
 * Builds the information-hiding view of `state` for `seat`. Safe to call
 * even when it isn't `seat`'s turn (e.g. for logging/UI) -- `isMyTurn`
 * tells the caller whether a decision is actually expected.
 */
export function botViewFor(state: HandState, seat: PlayerIndex): BotView {
  const opponent = otherSeat(seat);
  const players: [BotViewPlayer, BotViewPlayer] = [
    publicPlayer(state.players[0]),
    publicPlayer(state.players[1]),
  ];
  return {
    config: state.config,
    seat,
    opponent,
    button: state.button,
    street: state.street,
    hole: state.players[seat].hole,
    board: visibleBoardCards(state.deal, state.street),
    players,
    pot: state.players[0].totalCommitted + state.players[1].totalCommitted,
    minRaiseIncrement: state.minRaiseIncrement,
    actedThisStreet: state.actedThisStreet,
    toAct: state.toAct,
    actionLog: state.actionLog,
    legalActions: legalActions(state),
    startingStacks: state.startingStacks,
    isMyTurn: state.toAct === seat,
  };
}
